import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import VerificationInput from "react-verification-input";

import { ButtonClickCard } from "components";

import { useToast } from "hooks/toast";

import { LanguageSelector } from "components/LanguageSelector";

import { baseURL } from "services/api";
import { hearingFileService, hearingFileServiceV2 } from "services/hearingFile";
import {
  RecordingViewerAttachment,
  RecordingViewerContentResponse,
  RecordingViewerService,
} from "services/recordingViewer";

import kentaLogo from "assets/KENTA_4.0.png";

import {
  formatSubtitle,
  getFileNameFromPath,
  mapTypeIcon,
  mapTypeLabel,
} from "utils/fileDisplay";

import {
  Container,
  Content,
  LogoContainer,
  Title,
  Description,
  MediaWrapper,
  MessageWrapper,
  VideoPlayer,
  MessageBox,
  LoadingMessage,
  HearingMetadata,
  HearingMetadataItem,
  DownloadsSection,
  DownloadsTitle,
  DownloadsList,
  DownloadsHelper,
  MfaContainer,
  MfaDescription,
  VerificationWrapper,
  MfaButton,
  ResendCodeButton,
  MfaErrorText,
  LanguageSelectorWrapper,
} from "./styles";

const formatHearingDate = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsed);
  } catch {
    return value;
  }
};

const RecordingViewer = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [content, setContent] = useState<RecordingViewerContentResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [finalError, setFinalError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<RecordingViewerAttachment[]>(
    [],
  );
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  const [requiresMfa, setRequiresMfa] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);
  const { addToast } = useToast();

  const getToken = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("token");
  }, [location.search]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const loadContent = useCallback(
    async (code?: string) => {
      const token = getToken();

      if (!token) {
        setFinalError(t("recordingViewer.invalidToken"));
        setContent(null);
        return;
      }

      setIsLoading(true);
      setFinalError(null);
      setMfaError(null);

      try {
        const response = await RecordingViewerService.getContent(token, code);

        if (response.requiresMfa) {
          setRequiresMfa(true);
          if (response.cooldownSeconds) {
            setResendTimer(response.cooldownSeconds);
            addToast({
              type: "warning",
              title: t("auth.mfa.title"),
              description: response.message,
            });
          } else {
            setResendTimer(60);
            addToast({
              type: "success",
              title: t("auth.mfa.codeResent"),
              description: t("auth.mfa.codeResentDescription"),
            });
          }
          setIsLoading(false);
          return;
        }

        if (!response?.success || !response.data) {
          if (requiresMfa) {
            setMfaError(response?.message || t("recordingViewer.invalidCode"));
            setVerificationCode("");
          } else {
            setFinalError(response?.message || t("recordingViewer.loadError"));
            setContent(null);
          }
          setIsLoading(false);
          return;
        }

        setRequiresMfa(false);
        setContent(response.data);
      } catch {
        if (requiresMfa && code) {
          setMfaError(t("recordingViewer.verifyError"));
          setVerificationCode("");
        } else {
          setFinalError(t("recordingViewer.loadTokenError"));
          setContent(null);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getToken, requiresMfa, addToast],
  );

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleVerifyCode = () => {
    if (verificationCode.length !== 6) {
      setMfaError(t("recordingViewer.enterSixDigits"));
      return;
    }
    loadContent(verificationCode);
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setVerificationCode("");
    setMfaError(null);

    await loadContent();
  };

  const handleDownload = useCallback(
    (hearingFileId?: number) => {
      if (!hearingFileId) {
        addToast({
          type: "error",
          title: t("recordingViewer.downloadError"),
          description: t("recordingViewer.downloadIdentifyError"),
        });
        return;
      }

      const sharedToken = getToken() ?? undefined;
      hearingFileService
        .getDownloadToken(hearingFileId, sharedToken)
        .then((response) => {
          if (response.success && response.data) {
            const downloadUrl = `${baseURL}${response.data.downloadUrl}`;
            window.open(downloadUrl, "_blank");
          } else {
            addToast({
              type: "error",
              title: t("recordingViewer.downloadError"),
              description:
                response.message || t("recordingViewer.downloadLinkError"),
            });
          }
        })
        .catch(() => {
          addToast({
            type: "error",
            title: t("recordingViewer.downloadError"),
            description: t("recordingViewer.downloadFailed"),
          });
        });
    },
    [addToast],
  );

  // helpers moved to utils/fileDisplay

  useEffect(() => {
    const loadAttachments = async () => {
      if (!content || content.mediaType !== "VIDEO") {
        setAttachments([]);
        setAttachmentsLoading(false);
        return;
      }

      if (content.attachments?.length) {
        setAttachments(content.attachments);
        setAttachmentsLoading(false);
        return;
      }

      if (!content.hearingId) {
        setAttachments([]);
        setAttachmentsLoading(false);
        return;
      }

      setAttachmentsLoading(true);
      try {
        const { status, data } =
          await hearingFileServiceV2.getAllDownloadsByHearingId(
            content.hearingId,
          );
        if (status === 200) {
          setAttachments(data?.data ?? []);
        } else {
          setAttachments([]);
        }
      } catch {
        setAttachments([]);
      } finally {
        setAttachmentsLoading(false);
      }
    };

    loadAttachments();
  }, [content]);

  const languageSelector = (
    <LanguageSelectorWrapper>
      <LanguageSelector />
    </LanguageSelectorWrapper>
  );

  if (finalError) {
    return (
      <Container>
        {languageSelector}
        <Content>
          <LogoContainer>
            <img
              src={kentaLogo}
              alt="Logo da Empresa"
              width={240}
              height={90}
            />
          </LogoContainer>
          <Title>{t("common.attention")}</Title>
          <Description>{finalError}</Description>
        </Content>
      </Container>
    );
  }

  if (requiresMfa) {
    return (
      <Container>
        {languageSelector}
        <Content>
          <LogoContainer>
            <img
              src={kentaLogo}
              alt="Logo da Empresa"
              width={240}
              height={90}
            />
          </LogoContainer>
          <Title>{t("recordingViewer.securityVerification")}</Title>
          <MfaContainer>
            <MfaDescription>
              {t("recordingViewer.enterCodeDescription")}
            </MfaDescription>
            <VerificationWrapper>
              <VerificationInput
                value={verificationCode}
                onChange={setVerificationCode}
                length={6}
                placeholder="·"
                autoFocus
                validChars="0-9"
                classNames={{
                  container: "verification-container",
                  character: "verification-character",
                  characterInactive: "verification-character--inactive",
                  characterSelected: "verification-character--selected",
                  characterFilled: "verification-character--filled",
                }}
              />
            </VerificationWrapper>
            {mfaError && <MfaErrorText>{mfaError}</MfaErrorText>}
            <MfaButton
              onClick={handleVerifyCode}
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading
                ? t("recordingViewer.verifying")
                : t("recordingViewer.verifyCode")}
            </MfaButton>
            <ResendCodeButton
              onClick={handleResendCode}
              disabled={resendTimer > 0 || isLoading}
            >
              {resendTimer > 0
                ? t("auth.mfa.resendCodeTimer", { seconds: resendTimer })
                : t("common.resendCode")}
            </ResendCodeButton>
          </MfaContainer>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      {languageSelector}
      <Content>
        <LogoContainer>
          <img src={kentaLogo} alt="Logo da Empresa" width={240} height={90} />
        </LogoContainer>

        {content?.mediaType === "VIDEO" && (
          <Title>{t("audiences.recording")}</Title>
        )}

        {isLoading && (
          <LoadingMessage>{t("recordingViewer.loadingContent")}</LoadingMessage>
        )}

        {!isLoading &&
          content &&
          content.videoUrl &&
          (() => {
            const formattedDate = formatHearingDate(content.hearingDate);
            const formattedExpireDate = formatHearingDate(content.expireIn);
            const attachmentsToShow = attachments.filter(
              (attachment) => !attachment.url,
            );

            return (
              <MediaWrapper>
                <VideoPlayer
                  controls
                  controlsList="nodownload"
                  onContextMenu={(event) => event.preventDefault()}
                >
                  <source src={content.videoUrl} type="video/mp4" />
                  {t("recordingViewer.browserNotSupported")}
                </VideoPlayer>
                {(content.title || formattedDate || formattedExpireDate) && (
                  <HearingMetadata>
                    {content.title && (
                      <HearingMetadataItem>{content.title}</HearingMetadataItem>
                    )}
                    {formattedDate && (
                      <HearingMetadataItem>
                        {t("recordingViewer.recordingDate")}: {formattedDate}
                      </HearingMetadataItem>
                    )}
                    {formattedExpireDate && (
                      <HearingMetadataItem>
                        {t("recordingViewer.expiresIn")}: {formattedExpireDate}
                      </HearingMetadataItem>
                    )}
                  </HearingMetadata>
                )}
                <DownloadsSection>
                  {attachmentsLoading && (
                    <DownloadsHelper>
                      {t("recordingViewer.loadingAttachments")}
                    </DownloadsHelper>
                  )}

                  {!attachmentsLoading && attachmentsToShow.length > 0 && (
                    <>
                      <DownloadsTitle>
                        {t("audiences.attachments")}
                      </DownloadsTitle>
                      <DownloadsList>
                        {attachmentsToShow.map((attachment) => {
                          const displayName =
                            attachment.title ||
                            attachment.filename ||
                            getFileNameFromPath(attachment.url ?? undefined);
                          const subTitle = formatSubtitle(
                            mapTypeLabel(attachment.type),
                            attachment.size,
                          );

                          return (
                            <ButtonClickCard
                              key={attachment.hearingFileId}
                              title={displayName}
                              subTitle={subTitle}
                              tooltipMessagem={
                                attachment.hearingFileId
                                  ? t("recordingViewer.downloadFile")
                                  : t("recordingViewer.unavailable")
                              }
                              onClick={() => {
                                handleDownload(attachment.hearingFileId);
                              }}
                              typeButton="download"
                              typeIcon={mapTypeIcon(attachment.type)}
                            />
                          );
                        })}
                      </DownloadsList>
                    </>
                  )}
                </DownloadsSection>
              </MediaWrapper>
            );
          })()}

        {!isLoading && content && content.mediaType === "MESSAGE" && (
          <MessageWrapper>
            <MessageBox>
              {content.message ?? t("recordingViewer.noMessageAvailable")}
            </MessageBox>
          </MessageWrapper>
        )}

        {!isLoading && !content && (
          <MessageBox>{t("recordingViewer.noContentAvailable")}</MessageBox>
        )}
      </Content>
    </Container>
  );
};

export default RecordingViewer;
